import TableMui from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TablePagination from '@mui/material/TablePagination';
import TableRow from '@mui/material/TableRow';
import { PAGE_INITIAL_VALUE, ROWS_PER_PAGE_OPTION } from 'common/constants';
import { Fragment } from 'react';
import { useNavigate } from 'react-router-dom';
import { formatCNPJ, formatDate } from 'utils/formatDate';
import { formatCPF } from 'utils/formatters';
import { useStoreSelector } from 'v3/data/store';
import { setFilterCurrentPage, setFilterRowsPerPage } from 'v3/data/store/contracting/actions';
import { ContractingStateEnum } from 'v3/data/store/contracting/enums';
import { ContractingStateMapping } from 'v3/data/store/contracting/mapping';
import { getStorePermissions } from 'v3/data/store/stores/actions';
import { useBreakpoints } from 'v3/hooks/useBreakpoints';
import { RoutesEnum } from 'v3/routes/enum';
import { ContractingMobileTable } from 'v3/shared/components/MobileTable/ContractingMobileTable';
import { NoItemsFound } from 'v3/shared/components/NoItemsFound';
import { SubtitleMedium } from 'v3/shared/typography/SubtitleMedium';
import { theme } from 'v3/styles/global-styles';
import {
  AlignStatusContainer,
  PaperStyled,
  TableCellGroup,
  TableCellStyled,
  TablePaginationStyles,
  TableRowStyled,
} from '../styles';

export const ContractingTable = () => {
  const navigate = useNavigate();
  const { isStore } = getStorePermissions();
  const { items, count } = useStoreSelector((store) => store.contracting.list);
  const { filter } = useStoreSelector((store) => store.contracting);
  const { rowsPerPage, page } = filter;
  const { isDesktop } = useBreakpoints();

  const handleNavigation = (contractingKey: string) => {
    navigate(`${RoutesEnum.Contracting}/${contractingKey}`);
  };

  return (
    <Fragment>
      {items.isEmpty ? (
        <NoItemsFound />
      ) : isDesktop ? (
        <PaperStyled>
          <TableContainer sx={{ maxHeight: 450 }}>
            <TableMui stickyHeader aria-label="sticky tables">
              <TableHead>
                <TableRow>
                  <TableCellStyled>Cliente</TableCellStyled>
                  {!isStore && <TableCellStyled>Loja</TableCellStyled>}
                  <TableCellStyled>Data e hora</TableCellStyled>
                  <TableCellStyled>Status</TableCellStyled>
                </TableRow>
              </TableHead>

              <TableBody>
                {items.map((item, index) => {
                  const { date, time } = formatDate(item.createdAt);
                  const contractingStatusTitle =
                    ContractingStateMapping[item.status as ContractingStateEnum]?.title || 'Status desconhecido';

                  return (
                    <TableRowStyled
                      hover
                      key={item.contractingKey}
                      onClick={() => handleNavigation(item.contractingKey)}
                    >
                      <TableCellGroup key={`name-cpf-${index}`}>
                        <SubtitleMedium>{item.person.fullName}</SubtitleMedium>
                        <SubtitleMedium color={theme.palette.secondary.main}>
                          {formatCPF(item.person.cpf)}
                        </SubtitleMedium>
                      </TableCellGroup>

                      {!isStore && (
                        <TableCellGroup key={`storeName-cnpj-${index}`}>
                          <SubtitleMedium>{item.creditSimulation.store.name}</SubtitleMedium>
                          <SubtitleMedium color={theme.palette.secondary.main}>
                            {formatCNPJ(item.creditSimulation.store.cnpj)}
                          </SubtitleMedium>
                        </TableCellGroup>
                      )}

                      <TableCellGroup key={`date-time-${index}`}>
                        <SubtitleMedium>{date}</SubtitleMedium>
                        <SubtitleMedium color={theme.palette.secondary.main}>{time}</SubtitleMedium>
                      </TableCellGroup>

                      <TableCellGroup key={`status-${index}`}>
                        <AlignStatusContainer>
                          <SubtitleMedium>{contractingStatusTitle}</SubtitleMedium>
                        </AlignStatusContainer>
                      </TableCellGroup>
                    </TableRowStyled>
                  );
                })}
              </TableBody>
            </TableMui>
          </TableContainer>

          <TablePagination
            rowsPerPageOptions={ROWS_PER_PAGE_OPTION}
            component="div"
            sx={TablePaginationStyles}
            count={count}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={(event, value) => {
              setFilterCurrentPage(value);
            }}
            onRowsPerPageChange={(event) => {
              setFilterCurrentPage(PAGE_INITIAL_VALUE);
              setFilterRowsPerPage(Number(event.target.value));
            }}
          />
        </PaperStyled>
      ) : (
        <ContractingMobileTable contractingList={items} />
      )}
    </Fragment>
  );
};
